import { useState, useEffect, useCallback } from 'react';
import { Link2, Upload, Trash2, RefreshCw, FileSpreadsheet, Check, Info, Play, CheckCircle, XCircle, LogIn, LogOut, Settings } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Integrations() {
  const { getAuthHeader } = useAuth();
  const [sheetConfigs, setSheetConfigs] = useState([]);
  const [credentialsStatus, setCredentialsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingClient, setUploadingClient] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [formData, setFormData] = useState({ sheet_id: '', sheet_name: '', sheet_url: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [configsRes, credStatusRes] = await Promise.all([
        axios.get(`${API_URL}/api/sheets`, getAuthHeader()),
        axios.get(`${API_URL}/api/sheets/credentials-status`, getAuthHeader())
      ]);
      setSheetConfigs(configsRes.data);
      setCredentialsStatus(credStatusRes.data);
    } catch (error) {
      toast.error('Entegrasyonlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();

    // Check for OAuth callback code in URL
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      handleOAuthCallback(code);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchData]);

  const handleOAuthCallback = async (code) => {
    setConnecting(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/sheets/google-auth-callback?code=${encodeURIComponent(code)}`,
        {},
        getAuthHeader()
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google bağlantısı başarısız');
    } finally {
      setConnecting(false);
    }
  };

  const handleOAuthClientUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploadingClient(true);
    try {
      const response = await axios.post(`${API_URL}/api/sheets/upload-oauth-client`, formDataUpload, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'OAuth istemci dosyası yüklenemedi');
    } finally {
      setUploadingClient(false);
      e.target.value = '';
    }
  };

  const handleGoogleConnect = async () => {
    setConnecting(true);
    try {
      const response = await axios.get(`${API_URL}/api/sheets/google-auth-url`, getAuthHeader());
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google bağlantısı başlatılamadı');
      setConnecting(false);
    }
  };

  const handleGoogleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await axios.post(`${API_URL}/api/sheets/google-disconnect`, {}, getAuthHeader());
      toast.success('Google hesabı bağlantısı kesildi');
      fetchData();
    } catch (error) {
      toast.error('Bağlantı kesilemedi');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${API_URL}/api/sheets/upload`, formDataUpload, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Dosya yüklenemedi');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API_URL}/api/sheets/sync`, {}, getAuthHeader());
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senkronizasyon başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/sheets`, formData, getAuthHeader());
      toast.success('Google Sheets bağlantısı kaydedildi');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/sheets/${selectedConfig.id}`, getAuthHeader());
      toast.success('Yapılandırma silindi');
      setIsDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="integrations-loading">
        <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const hasOAuth = credentialsStatus?.has_oauth;
  const hasOAuthClient = credentialsStatus?.has_oauth_client;
  const hasCredentials = credentialsStatus?.has_credentials;
  const hasSheetConfig = sheetConfigs.length > 0;

  return (
    <div className="space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold font-[Manrope] tracking-tight">Google Sheets Entegrasyonu</h2>
          <p className="text-muted-foreground">Google hesabınızla doğrudan e-tablolardan veri çekin</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-integrations">
          <RefreshCw className="h-4 w-4 mr-2" />Yenile
        </Button>
      </div>

      {/* Google Account Connection Status */}
      <Card className={hasOAuth
        ? "border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20"
        : "border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20"
      } data-testid="google-connection-card">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div className="flex gap-4">
              {hasOAuth ? (
                <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
              ) : (
                <LogIn className="h-6 w-6 text-amber-500 flex-shrink-0" />
              )}
              <div>
                {hasOAuth ? (
                  <>
                    <p className="font-medium text-green-700 dark:text-green-400">
                      Google Hesabı Bağlı
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-500 mt-1">
                      {credentialsStatus?.oauth_email} hesabı ile bağlantı aktif
                    </p>
                  </>
                ) : hasOAuthClient ? (
                  <>
                    <p className="font-medium text-amber-700 dark:text-amber-400">
                      Google Hesabınızı Bağlayın
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                      OAuth istemci yapılandırması hazır. "Google ile Bağlan" butonuna tıklayın.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-amber-700 dark:text-amber-400">
                      Google OAuth İstemci Dosyası Gerekli
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-500 mt-1">
                      Google Cloud Console'dan OAuth 2.0 istemci kimlik dosyasını yükleyin
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {hasOAuth ? (
                <Button
                  variant="outline"
                  onClick={handleGoogleDisconnect}
                  disabled={disconnecting}
                  className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  data-testid="google-disconnect-btn"
                >
                  {disconnecting ? (
                    <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <LogOut className="h-4 w-4 mr-2" />
                  )}
                  Bağlantıyı Kes
                </Button>
              ) : hasOAuthClient ? (
                <Button
                  onClick={handleGoogleConnect}
                  disabled={connecting}
                  data-testid="google-connect-btn"
                >
                  {connecting ? (
                    <>
                      <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                      Bağlanıyor...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Google ile Bağlan
                    </>
                  )}
                </Button>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleOAuthClientUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={uploadingClient}
                    data-testid="upload-oauth-client-input"
                  />
                  <Button disabled={uploadingClient} data-testid="upload-oauth-client-btn">
                    {uploadingClient ? (
                      <>
                        <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Yükleniyor...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        OAuth İstemci Dosyası Yükle
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Instructions */}
      {!hasOAuth && !hasOAuthClient && (
        <Card data-testid="setup-instructions">
          <CardHeader>
            <CardTitle className="font-[Manrope]">Kurulum Adımları</CardTitle>
            <CardDescription>Google Sheets erişimi için OAuth 2.0 istemci oluşturun</CardDescription>
          </CardHeader>
          <CardContent>
            <ol className="list-decimal list-inside space-y-3 text-sm">
              <li>
                <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Google Cloud Console
                </a>'a gidin
              </li>
              <li>Mevcut projenizi seçin veya yeni bir proje oluşturun</li>
              <li>"APIs & Services" &rarr; "Enable APIs" &rarr; <strong>Google Sheets API</strong>'yi etkinleştirin</li>
              <li>"APIs & Services" &rarr; "Credentials" &rarr; "Create Credentials" &rarr; <strong>OAuth 2.0 Client ID</strong></li>
              <li>Uygulama türü: <strong>Web Application</strong> seçin</li>
              <li>
                "Authorized redirect URIs" kısmına ekleyin:
                <code className="block mt-1 p-2 bg-muted rounded text-xs break-all">
                  {window.location.origin}/admin/integrations
                </code>
              </li>
              <li>İndirilen JSON dosyasını yukarıdaki butona tıklayarak yükleyin</li>
            </ol>
          </CardContent>
        </Card>
      )}

      {/* Column Info Card */}
      <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-blue-700 dark:text-blue-400">Sütun Eşleştirme Bilgisi</p>
              <div className="text-sm text-blue-600 dark:text-blue-500 mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <p><strong>A sütunu:</strong> Bilet satın alma tarihi</p>
                <p><strong>D sütunu:</strong> Hasta ödeme tutarı (gelir)</p>
                <p><strong>F sütunu:</strong> Tedavi tarihi (komisyon hesabı)</p>
                <p><strong>M sütunu:</strong> Personel e-postası</p>
                <p><strong>P sütunu:</strong> Satış türü (SATIŞ, REFERANS, ELSA vb.)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sheet Configuration */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
            <div>
              <CardTitle className="font-[Manrope]">Google Sheets Bağlantısı</CardTitle>
              <CardDescription>Veri çekilecek tablo bilgilerini girin</CardDescription>
            </div>
            {!hasSheetConfig && (
              <Button onClick={() => setIsDialogOpen(true)} data-testid="add-sheet-btn">
                <Link2 className="h-4 w-4 mr-2" />Sheet Bağla
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {sheetConfigs.length === 0 ? (
            <div className="text-center py-8">
              <Link2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">Henüz Google Sheets bağlantısı yok</p>
              <Button onClick={() => setIsDialogOpen(true)} variant="outline" data-testid="empty-add-sheet">
                <Link2 className="h-4 w-4 mr-2" />Sheet Bağla
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {sheetConfigs.map((config) => (
                <div key={config.id} className="border rounded-lg p-4" data-testid={`sheet-config-${config.id}`}>
                  <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex-1">
                      <h4 className="font-medium">{config.sheet_name}</h4>
                      <p className="text-sm text-muted-foreground font-mono mt-1">ID: {config.sheet_id}</p>
                      {config.last_synced && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Son senkronizasyon: {new Date(config.last_synced).toLocaleString('tr-TR')}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSync}
                        disabled={syncing || !hasCredentials}
                        data-testid="sync-btn"
                      >
                        {syncing ? (
                          <>
                            <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                            Senkronize Ediliyor...
                          </>
                        ) : (
                          <><Play className="h-4 w-4 mr-2" />Senkronize Et</>
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setIsDialogOpen(true)} data-testid="edit-sheet-btn">
                        Düzenle
                      </Button>
                      <Button
                        variant="ghost" size="icon"
                        onClick={() => { setSelectedConfig(config); setIsDeleteDialogOpen(true); }}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-sheet-${config.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {!hasCredentials && (
                    <p className="text-sm text-amber-600 mt-3">
                      Senkronizasyon için önce Google hesabınızı bağlayın
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manual Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-[Manrope]">Manuel Veri Yükleme</CardTitle>
          <CardDescription>Alternatif olarak, Excel/CSV dosyasını doğrudan yükleyebilirsiniz</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center">
            <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground mb-4">Excel veya CSV dosyasını yükleyin</p>
            <div className="relative inline-block">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                disabled={uploading}
                data-testid="upload-sheet-input"
              />
              <Button variant="outline" disabled={uploading} data-testid="upload-sheet-btn">
                {uploading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Yükleniyor...
                  </>
                ) : (
                  <><Upload className="h-4 w-4 mr-2" />Dosya Seç</>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Desteklenen formatlar: .xlsx, .xls, .csv</p>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Sheet Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="sheet-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Google Sheets Bağlantısı</DialogTitle>
            <DialogDescription>Google Sheets bilgilerini girin</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sheet_name">Tablo Adı</Label>
                <Input id="sheet_name" value={formData.sheet_name} onChange={(e) => setFormData({ ...formData, sheet_name: e.target.value })} placeholder="Satış Verileri 2024" required data-testid="sheet-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sheet_id">Google Sheet ID</Label>
                <Input id="sheet_id" value={formData.sheet_id} onChange={(e) => setFormData({ ...formData, sheet_id: e.target.value })} placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms" required data-testid="sheet-id-input" />
                <p className="text-xs text-muted-foreground">URL'deki <code>/d/</code> ile <code>/edit</code> arasındaki değer</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sheet_url">Sheet URL (Opsiyonel)</Label>
                <Input id="sheet_url" value={formData.sheet_url} onChange={(e) => setFormData({ ...formData, sheet_url: e.target.value })} placeholder="https://docs.google.com/spreadsheets/d/..." data-testid="sheet-url-input" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
              <Button type="submit" disabled={saving} data-testid="save-sheet-btn">
                {saving ? 'Kaydediliyor...' : <><Check className="h-4 w-4 mr-2" />Kaydet</>}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-sheet-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bağlantıyı Sil</AlertDialogTitle>
            <AlertDialogDescription>Bu bağlantıyı silmek istediğinizden emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-sheet">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
